
/**
 * TODO: This is a simplified generic solution
 * TODO: It is lacking error handling and robust input checks, among other things
 * TODO: We could include a solution to automatically retrieve a new record copy upon a sync issue.
 */
const useCBAPI = (Vue, {
  basePath = process.env.VUE_APP_CBAPI_BASE_URL,
  tenant = process.env.VUE_APP_TENANT,
  apiKey = process.env.VUE_APP_CBAPI_API_KEY,
  version = 'v1',
  parseBody = true,
  catchError = true,
  logError = true,
  errorHandler = null,
  errorLogger = null,
}) => {

  /**
   * Default options for an API Call
   */
  let _defaultCallOptions = {
    parseBody,
    catchError,
    logError,
    errorHandler,
    errorLogger,
    basePath,
    tenant,
    apiKey,
    version,
  }

  /****************************************************************************
   * Internal helper and generic functions
   */

  /**
   * Obtain the Authhorization header
   *
   * @returns {string} The bearer
   */
  async function getAuth0Header() {
    if (! Vue.prototype.$auth) {
      throw new Error('An Auth0 token is required to make API calls')
    }
    let token = await Vue.prototype.$auth.getTokenSilently()
    return `Bearer ${token}`
  }

  /**
   * A key-value list of query string values.
   *  Values that are null are ignored
   *
   * @param {*} params
   * @returns {string}
   */
  function queryStringFromOptionalParams(params) {
    let queryParams = new URLSearchParams()

    Object.keys(params)
      .filter(param => {
        return params[param] !== null
          && params[param] !== undefined
      })
      .forEach(param => {
        queryParams.append(param, params[param])
      })

    return `?${queryParams.toString()}`
  }

  /**
   * Generate the path to the API Endpoint
   */
  function getAPIEndpointPath({
    basePath,
    version,
    tenant,
    endpoint,
  }) {
    basePath = basePath || _defaultCallOptions.basePath
    version = version || _defaultCallOptions.version
    tenant = tenant || _defaultCallOptions.tenant
    return `${basePath}/${version}/${tenant}/${endpoint}`
  }

  /**
   * Generic function to standardize API calls
   */
  async function call({
    endpoint,
    method = 'GET',
    body = null,
    options = {},
  }) {

    options = {
      ..._defaultCallOptions,
      ...options,
    }
    method = method.toUpperCase()
    if (method === 'GET') {
      body = null
    }

    if (body !== null && typeof string !== 'string') {
      body = JSON.stringify(body)
    }

    return await fetch(
      getAPIEndpointPath({
        basePath: options.basePath,
        version: options.version,
        tenant: options.tenant,
        endpoint,
      }),
      {
        method,
        headers: {
          Authorization: await getAuth0Header(),
          'X-API-KEY': options.apiKey,
        },
        body,
      },
    )
    .then(async response => {
      /**
       * Just wear pink glasses and all will be well
       */
      if (! options.catchError) {
        if (options.parseBody) {
          let text = await response.text()
          try {
            return JSON.parse(text)
          } catch (e) {
            return text
          }
        }

        return response
      }

      /**
       * The API call didn't succeed for some reason
       */
      if (! response.ok) {

        if (options.logError) {
          if (options.errorLogger) {
            options.errorLogger({
              endpoint,
              method,
              body,
              error: null,
              response,
            })
          } else {
            console.log('Call Failed', endpoint)
          }
        }

        if (options.errorHandler) {
          return options.errorHandler({
            endpoint,
            method,
            body,
            error: null,
            response,
          })
        }

        return false
      }

      if (options.parseBody) {
        let text = await response.text()
        try {
          return JSON.parse(text)
        } catch (e) {
          return text
        }
      }
      return response
    })
    .catch(e => {
      // Just rethrow the error if the call is configured not to handle errors
      if (! options.catchError) {
        throw e
      }

      if (options.logError) {
        if (options.errorLogger) {
          options.errorLogger({
            endpoint,
            method,
            body,
            error: e,
            response: null,
          })
        } else {
          console.log('Call Failed', endpoint)
        }
      }

      if (options.errorHandler) {
        return options.errorHandler({
          endpoint,
          method,
          body,
          error: e,
          response: null,
        })
      }

      // Not great...
      console.log(e)
      return false
    })

  }

  /**
   * GET Helper
   */
  async function get({ endpoint, options }) {
    return await call({
      endpoint,
      method: 'GET',
      body: null,
      options,
    })
  }

  /**
   * POST helper
   */
  async function post({ endpoint, body, options }) {
    return await call({
      endpoint,
      method: 'POST',
      body,
      options,
    })
  }

  /****************************************************************************
   * Comment methods generator
   */

  /**
   * The comments endpoint contracts are identical for multiple entities
   */
  function generateCommentMethods({ entity }) {
    return {
      /**
       * Get the comments of a specific record
       */
      async getComments({ uuid }) {
        return await get({
          endpoint: `${entity}/${uuid}/comments`,
        })
      },
      /**
       * Add a comment
       */
      async add({
        uuid,
        message,
        status,
        username = null,
        synced, // the record TS
      }) {
        return await post({
          endpoint: `${entity}/${uuid}/comments`,
          body: {
            synchronisation: {
              synced,
            },
            data: {
              action: 'create',
              message,
              status,
              number: null,
              username,
            },
          },
        })
      },
      /**
       * Edit a comment
       */
      async edit({
        uuid,
        message,
        status,
        number,
        username = null,
        synced, // the record TS
      }) {
        return await post({
          endpoint: `${entity}/${uuid}/comments`,
          body: {
            synchronisation: {
              synced,
            },
            data: {
              action: 'edit',
              message,
              status,
              number,
              username,
            },
          },
        })
      },
      /**
       * Change the status of a comment
       */
      async status({
        uuid,
        status,
        number,
        username = null,
        synced, // the record TS
      }) {
        return await post({
          endpoint: `${entity}/${uuid}/comments`,
          body: {
            synchronisation: {
              synced,
            },
            data: {
              action: 'status',
              message: null,
              status,
              username,
              number,
            },
          },
        })
      },
      /**
       * Delete a comment
       */
      async del({
        uuid,
        number,
        username = null,
        synced, // the record TS
      }) {
        return await post({
          endpoint: `${entity}/${uuid}/comments`,
          body: {
            synchronisation: {
              synced,
            },
            data: {
              action: 'delete',
              message: null,
              status: null,
              username,
              number,
            },
          },
        })
      },
    }
  }


  /****************************************************************************
   * Public methods
   */
  return {

    /**
     * File related interactions
     */
    files: {
      /**
       * Get details of a specific file
       */
      async getInformationByUuid({ uuid }) {
        return get({
          endpoint: `files/${uuid}`,
        })
      },

      /**
       * Get the download link of a specific file
       *  Note: the link is only valid for a short time
       */
      async getDownloadLinkByUuid({ uuid }) {
        return get({
          endpoint: `files/${uuid}/download-link`,
        })
      },

      /**
       * Get an upload link for a file related to a process
       *  Note: the link is only valid for a short time
       */
      async getProcessUploadLink({
        processUuid,
        filename, // without ext
        extension,
        category,
      }) {
        return post({
          endpoint: `processes/realisation/${processUuid}/files/upload-link`,
          body: {
            filename,
            extension,
            category,
          },
        })
      },

      // TODO: "Delete" a file - endpoint doesn't exist yet
      async disconnectFileFromProcess({
        processUuid,
        fileUuid,
        synced,
      }) {
        return post({
          endpoint: `processes/realisation/${processUuid}/files/${fileUuid}`,
          body: {
            synchronisation: {
              synced,
            },
            data: {
              action: 'disconnect',
            },
          },
        })
      },
    },

    /**
     * Location related interactions
     */
    locations: {
      /**
       * Get a page from the Locations collection
       */
      async getCollectionPage({
        fromTimeStamp,
        offSet,
        at,
        status,
        codes,
        pageSize = 400,
      }) {
        if (pageSize > 400) {
          throw new Error('Max page size is 2500')
        }
        let queryString = queryStringFromOptionalParams({
          pageSize,
          at,
          fromTimeStamp,
          offSet,
          codes,
          status,
        })

        return await get({
          endpoint: `charging-point-locations${queryString}`,
        })
      },

      /**
       * Get the information of a specific location
       */
      async getByUuid({ uuid }) {
        return await get({
          endpoint: `charging-point-locations/${uuid}`,
        })
      },
    },
    /**
     * Charging Point Request related interactions
     */
    requests: {
      /**
       * Get a page from the charging point requests collection
       */
      async getCollectionPage({
        fromTimeStamp,
        offSet,
        at,
        codes,
        pageSize = 400,
      }) {
        if (pageSize > 400) {
          throw new Error('Max page size is 2500')
        }
        let queryString = queryStringFromOptionalParams({
          pageSize,
          at,
          fromTimeStamp,
          offSet,
          codes,
        })

        return await get({
          endpoint: `charging-point-requests${queryString}`,
        })
      },

      /**
       * Get the information of a specific charging point request
       */
      async getByUuid({ uuid }) {
        return await get({
          endpoint: `charging-point-requests/${uuid}`,
        })
      },

      /**
       * Add a judgement to a particular charging point request
       */
      async submitJudgement({
        uuid,
        status,
        reason,
        remarks,
        synced,
      }) {
        return await post({
          endpoint: `charging-point-requests/${uuid}/judgement`,
          body: {
            synchronisation: {
              synced,
            },
            // TODO: Refactor CBAPI to remove need for action. Discuss with Vattenfall first
            intent: {
              action: 'judgement',
            },
            data: {
              status,
              reason,
              remarks,
            },
          },
        })
      },

      /**
       * Replace the connected tags of a charging point request
       * TODO: Create charging-point-request version of endpoint
       */
      async setTags({ uuid, synced, tags }) {
        return await post({
          endpoint: `charging-point-requests/${uuid}/tags`,
          body: {
            synchronisation: {
              synced,
            },
            data: {
              tags,
            },
          },
        })
      },

      /**
       * Manually set / override the coordinates of a charging point request location
       */
      async setCoordinates({ uuid, synced, coordinates }) {
        return await post({
          endpoint: `charging-point-requests/${uuid}/coordinates`,
          body: {
            synchronisation: {
              synced,
            },
            data: {
              latitude: coordinates.lat,
              longitude: coordinates.lng,
            },
          },
        })
      },

      /**
       * Manually trigger the geocoder for a single request, in case it failed to run automatically
       */
      async runGeocoder({ uuid }) {
        return await get({
          endpoint: `charging-point-requests/${uuid}/geocoder`,
        })
      },

      async fetchCoordinatesFromGeocoder({ uuid, synced }) {
        return await post({
          endpoint: 'charging-point-requests/geocoder',
          body: {
            synchronisation: {
              synced,
            },
            data: {
              uuids: [uuid],
            },
          },
        })
      },


      /**
       * Comment related endpoints for Charging Point Requests
       */
      comments: generateCommentMethods({
        entity: 'charging-point-requests',
      }),
    },

    /**
     * Realisation Process related interactions
     */
    process: {

      /**
       * Get a page from the realisation process collection
       */
      async getCollectionPage({
        fromTimeStamp,
        offSet,
        at,
        codes,
        pageSize = 400,
        locationMapper = null,
      }) {
        if (pageSize > 400) {
          throw new Error('Max page size is 1000')
        }
        let queryString = queryStringFromOptionalParams({
          pageSize,
          at,
          fromTimeStamp,
          offSet,
          codes,
          locationMapper,
        })

        return await get({
          endpoint: `processes/realisation/${queryString}`,
        })
      },

      /**
       * Get a single realisation process by uuid
       */
      async getByUuid({ uuid }) {
        return get({
          endpoint: `processes/realisation/${uuid}`,
        })
      },

      /**
       * Replace the connected tags of a realisation process
       */
      async setTags({ uuid, synced, tags }) {
        return await post({
          endpoint: `processes/realisation/${uuid}/tags`,
          body: {
            synchronisation: {
              synced,
            },
            data: {
              tags,
            },
          },
        })
      },

      /**
       * Connect or disconnect a location to / from a realisation process
       */
      async setLocationConnection({
        uuid,
        locationUuid,
        action,
        category,
        synced,
        locationSynced,
      }) {
        if (! ['connect', 'disconnect'].includes(action)) {
          throw new Error('Invalid action')
        }
        if (! ['selected', 'alternative'].includes(category)) {
          throw new Error('Invalid category')
        }

        return await post({
          endpoint: `processes/realisation/${uuid}/locations/${locationUuid}`,
          body: {
            synchronisation: {
              synced,
              LocationModel: locationSynced,
            },
            data: {
              action,
              category,
            },
          },
        })
      },

      /**
       * Connect or disconnect a request to / from a realisation process
       */
      async setRequestConnection({
        uuid,
        requestUuid,
        synced,
        requestSynced,
        action,
      }) {
        if (! ['connect', 'disconnect'].includes(action)) {
          throw new Error('Invalid action')
        }

        return await post({
          endpoint: `processes/realisation/${uuid}/requests/${requestUuid}`,
          body: {
            synchronisation: {
              synced,
              ChargingPointRequestModel: requestSynced,
            },
            data: {
              action,
            },
          },
        })
      },

      /**
       * Enable or disable a status of a realisation process
       */
      async setStatus({
        uuid,
        synced,
        status,
        action,
        reason,
        comment,
      }) {
        if (! ['cancelled', 'onhold', 'vkb'].includes(status)) {
          throw new Error('Invalid status')
        }
        if (! ['enable', 'disable'].includes(action)) {
          throw new Error('Invalid action')
        }

        let data = {
          status,
          action,
        }

        // Optional data
        if (reason) {
          data.reason = reason
        }
        if (comment) {
          data.comment = comment
        }

        return await post({
          endpoint: `processes/realisation/${uuid}/status`,
          body: {
            synchronisation: {
              synced,
            },
            data,
          },
        })
      },

      /**
       * Create a new Realisation Process
       */
      async create({
        geographyCode,
        stakeholders,
        tenant,
        type,
        subtype,
      }) {
        return await post({
          endpoint: 'processes/realisation',
          body: {
            geographyCode,
            stakeholders,
            tenant,
            type,
            subtype,
          },
        })
      },

      /**
       * Save a Realisation Process step
       */
      async saveStep({
        uuid,
        intent,
        synced,
        data,
      }) {
        return await post({
          endpoint: `processes/realisation/${uuid}`,
          body: {
            intent: {
              step: intent.step,
              complete: intent.complete,
              correction: intent.correction,
              target: intent.target,
            },
            synchronisation: {
              synced,
            },
            data,
          },
        })
      },

      /**
       * Comment related endpoints for Realisation Processes
       */
      comments: generateCommentMethods({
        entity: 'processes/realisation',
      }),

      // Realisation Process update stakeholders (only contractors and gridoperators)
      async updateStakeholder({ uuid, synced, slug }) {
        return await post({
          endpoint: `processes/realisation/${uuid}/stakeholders`,
          body: {
            synchronisation: {
              synced,
            },
            data: {
              slug,
            },
          },
        })
      },
    },

    /**
     * Config related interactions
     */
    config: {

      /**
       * Get the license configs relevant to the current context
       * TODO: Create endpoint
       * Note: In the new setup this may return multiple license configurations
       */
      async getLicenseDetails() {
        return get({
          endpoint: 'configs',
        })
      },

      /**
       * Get a process specification by Uuid
       */
      async getProcessSpecificationByUuid({
        uuid,
      }) {
        return get({
          endpoint: `processes/specification/${uuid}`,
        })
      },

      /**
       * Retrieve all stakeholders relevant to the current context
       */
      async getStakeholders() {
        return await get({
          endpoint: 'stakeholders',
        })
      },

      /**
       * Add a stakeholder
       *
       * TODO: Enable adding of stakeholders through Workflow Backoffice
       */
      async addStakeholder({
        name, category,
      }) {
        if (! ['cpo', 'municipality', 'customer', 'contractor', 'gridoperator'].includes(category)) {
          throw new Error('Invalid category')
        }

        return await post({
          endpoint: 'stakeholders',
          body: {
            name,
            category,
          },
        })
      },

      /**
       * Retrieve all tags relevant to the current context
       * TODO: Add tags endpoints
       */
      async getTags() {
        return await get({
          endpoint: 'tags',
        })
      },

      /**
       * Add a tag
       *
       * TODO: Add tags endpoints
       */
      async addTag({
        label,
      }) {
        return await post({
          endpoint: 'tags',
          body: {
            label,
          },
        })
      },
    },

    manual: {
      async getPage({ slug }) {
        return await get({
          endpoint: `manual/page/${slug}`,
        })
      },
    },
    user: {
      async acceptTerms({ version }) {
        return await post({
          endpoint: 'user/accept-terms',
          body: {
            version,
          },
          options: {
            tenant: 'utility',
          },
        })
      },
      async resetPassword({ password }) {
        return await post({
          endpoint: 'user/password-reset',
          body: {
            password,
          },
          options: {
            tenant: 'utility',
          },
        })
      },
    },

    /**
     * Various supporting functionality
     */
    utility: {

      getAPIEndpointPath({ endpoint }) {
        return getAPIEndpointPath({
          endpoint,
        })
      },

      /**
       * A pagination helper
       */
      async paginatedAPICall({
        apiCall,
        params = {},
        progressCallback = null,
        page = 0,
        count = 0,
        at,
        offSet,
        related = false,
      }) {
        // update the pagination params
        if (at) {
          params.at = at
        }
        if (offSet) {
          params.offSet = offSet
        }

        // Make the call
        let response = await apiCall(params)

        // Update count
        count = response?.data?.length + count
        page++

        // Report progress if applicable
        if (progressCallback) {
          progressCallback({
            page,
            count,
          })
        }

        let records = response.data
        if (related) {
          records = {
            records: response.data,
            related: response.related || {},
          }
        }

        // Get the next page if there is one
        if (response?.metaData?.pagination?.next) {
          const accumulatedRecords = await this.paginatedAPICall({
            apiCall,
            params,
            progressCallback,
            page,
            count,
            // Pass on the received pagination params
            at: response.metaData.pagination.at,
            offSet: response.metaData.pagination.next,
            related,
          })

          if (related) {
            Object.keys(records.related).forEach(key => {
              accumulatedRecords.related[key] = [
                ...(accumulatedRecords.related[key] || []),
                ...records.related[key],
              ]
            })

            return {
              records: [...records.records, ...(accumulatedRecords.records || [])],
              related: accumulatedRecords.related,
            }
          } else {
            return records.concat(accumulatedRecords)
          }
        }

        return records
      },
    },
  }
}

/**
 * Register the API as Vue plugin
 */
export const CBAPIPlugin = {
  install(Vue, options = {}) {
    Vue.prototype.$cbapi = useCBAPI(Vue, options)
  },
}
